import { useState, type ReactElement } from "react";

import { Button } from "../components/Button";
import { requestAccess, type SessionIdentity } from "../lib/auth/api";
import { useAuth } from "../lib/auth/AuthContext";

export function AccessRequestPage({ session }: { session: SessionIdentity }): ReactElement {
  const { logout } = useAuth();
  const [message, setMessage] = useState("");
  const [requestStatus, setRequestStatus] = useState(session.access_request_status ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disabled = session.access_status === "DISABLED";
  const inactive = session.access_status === "INACTIVE";
  const pending = requestStatus === "PENDING";

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const result = await requestAccess(message);
      setRequestStatus(result.status as "PENDING");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not submit the access request");
    } finally {
      setSubmitting(false);
    }
  }

  async function signOut(): Promise<void> {
    await logout();
    window.location.assign("/workspace/login");
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-[var(--color-surface-subtle,#f4f4f2)] px-5 py-10">
      <section className="w-full max-w-xl border border-[var(--color-border-default)] bg-[var(--color-surface-base,#fff)] p-8 shadow-[var(--shadow-md)]">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-accent)]">Velocity Workspace</p>
        <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
          {disabled ? "Access has been removed" : pending ? "Your request is awaiting approval" : inactive ? "Your account is inactive" : "Request access"}
        </h1>
        <p className="mt-3 text-sm leading-6 text-[var(--color-text-secondary)]">
          {disabled
            ? "A SuperAdmin has disabled this account. Contact the workspace administrator if you believe this is a mistake."
            : pending
              ? "A SuperAdmin will assign your role. You will receive an email when access is approved."
              : inactive ? "You are onboarded, but a SuperAdmin must assign an active role before you can enter. You can request access below."
              : "Your Microsoft Entra sign-in is verified, but a SuperAdmin must approve a platform role before you can enter."}
        </p>

        <dl className="mt-6 grid gap-2 border-y border-[var(--color-border-subtle)] py-4 text-sm">
          <div className="flex justify-between gap-4"><dt className="text-[var(--color-text-secondary)]">Signed in as</dt><dd className="text-right font-medium">{session.name || session.email || session.sub}</dd></div>
          {session.email && <div className="flex justify-between gap-4"><dt className="text-[var(--color-text-secondary)]">Email</dt><dd className="text-right">{session.email}</dd></div>}
          <div className="flex justify-between gap-4"><dt className="text-[var(--color-text-secondary)]">Status</dt><dd className="text-right font-medium">{disabled ? "Disabled" : pending ? "Pending approval" : inactive ? "Inactive" : "Not requested"}</dd></div>
        </dl>

        {!disabled && !pending && (
          <label className="mt-6 block text-sm font-medium text-[var(--color-text-primary)]">
            Message to the administrator <span className="font-normal text-[var(--color-text-secondary)]">(optional)</span>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              maxLength={1000}
              rows={4}
              className="mt-2 w-full border border-[var(--color-border-default)] bg-transparent px-3 py-2 font-normal outline-none focus:border-[var(--color-accent)]"
              placeholder="Tell the administrator why you need access"
            />
          </label>
        )}
        {error && <p role="alert" className="mt-3 text-sm text-[var(--color-text-danger,#b1382c)]">{error}</p>}

        <div className="mt-6 flex flex-wrap gap-3">
          {!disabled && !pending && (
            <Button
              type="primary"
              shape="rounded"
              text={submitting ? "Submitting…" : "Request access"}
              disabled={submitting}
              aria-busy={submitting || undefined}
              onClick={() => void submit()}
            />
          )}
          {pending && (
            <Button
              type="primary"
              shape="rounded"
              text="Check access"
              onClick={() => window.location.reload()}
            />
          )}
          <Button type="secondary" shape="rounded" text="Sign out" onClick={() => void signOut()} />
        </div>
      </section>
    </main>
  );
}
